import { ChangeDetectionStrategy, Component, computed, forwardRef, input, signal } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import dayjs from 'dayjs';
import { ControlValueAccessorBase } from '../../../core/control-value-accessor-base';

interface CalendarDay {
  date: Date;
  label: number;
  inCurrentMonth: boolean;
  disabled: boolean;
  selected: boolean;
  today: boolean;
}

@Component({
  selector: 'sc-calendar',
  templateUrl: './calendar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => CalendarComponent),
    },
  ],
})
export class CalendarComponent extends ControlValueAccessorBase<Date | null> {
  public minDate = input<dayjs.Dayjs | null>(null);
  public maxDate = input<dayjs.Dayjs | null>(null);

  public viewMonth = signal(dayjs().startOf('month'));

  private selectedDate = signal<Date | null>(null);

  public monthLabel = computed(() => this.viewMonth().format('MMMM YYYY'));

  public weekdays = computed(() => {
    const start = this.viewMonth().startOf('week');
    return Array.from({ length: 7 }, (_, i) => start.add(i, 'day').format('dd'));
  });

  public days = computed<CalendarDay[]>(() => {
    const month = this.viewMonth();
    const min = this.minDate();
    const max = this.maxDate();
    const selected = this.selectedDate();
    const today = dayjs();

    const gridStart = month.startOf('month').startOf('week');
    const gridEnd = month.endOf('month').endOf('week');
    const length = gridEnd.diff(gridStart, 'day') + 1;

    return Array.from({ length }, (_, i) => {
      const day = gridStart.add(i, 'day');

      return {
        date: day.toDate(),
        label: day.date(),
        inCurrentMonth: day.isSame(month, 'month'),
        disabled: (!!min && day.isBefore(min, 'day')) || (!!max && day.isAfter(max, 'day')),
        selected: !!selected && day.isSame(selected, 'day'),
        today: day.isSame(today, 'day'),
      };
    });
  });

  public previousMonth() {
    this.viewMonth.update((month) => month.subtract(1, 'month'));
  }

  public nextMonth() {
    this.viewMonth.update((month) => month.add(1, 'month'));
  }

  public selectDay(day: CalendarDay) {
    if (day.disabled) {
      return;
    }

    super.writeValue(day.date);
    this.selectedDate.set(day.date);
    this.onChange(day.date);
  }

  override writeValue(value: Date | null): void {
    super.writeValue(value);
    this.selectedDate.set(value);

    if (value) {
      this.viewMonth.set(dayjs(value).startOf('month'));
    }
  }
}
